import { computed, effect, inject, Injectable } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, map, scan } from 'rxjs';
import { DeclinableProductStepperStore } from '../declinable-product-stepper.store';
import { RootStepperStore } from '../root-stepper.store';

@Injectable()
export class DeclinableProductBlockAutoNextStepService {
  private readonly declinableProductStepperStore = inject(DeclinableProductStepperStore);
  private readonly rootStepperStore = inject(RootStepperStore);

  constructor() {
    effect(
      () =>
        this.rootStepperStore.isAutoNextStepBlocked.update(state => ({
          ...state,
          isDeclinableProductStepperBlocked: this.blockAutoNextStep(),
        })),
      {
        allowSignalWrites: true,
      },
    );
  }

  private readonly selectedStepsId$ = toObservable(
    this.declinableProductStepperStore.selectedStepsId,
  );

  public readonly selectableCategoryIds$ = toObservable(
    this.declinableProductStepperStore.selectableCategoryIds,
  );

  private readonly additionsGroupOfTheCurrentStep$ = toObservable(
    this.declinableProductStepperStore.steppers,
  ).pipe(
    map(steppers => {
      return steppers?.simpleProductStepper?.additionsGroups;
    }),
  );

  private readonly categoryIdStepBeforeBack = toSignal(
    combineLatest({
      selectedStepsId: this.selectedStepsId$,
      selectableCategoryIds: this.selectableCategoryIds$,
      additionsGroups: this.additionsGroupOfTheCurrentStep$,
    }).pipe(
      map(({ selectedStepsId, selectableCategoryIds, additionsGroups }) => {
        if (!selectedStepsId || !selectableCategoryIds) {
          return undefined;
        }
        const position = selectableCategoryIds.indexOf(selectedStepsId.categoryId);
        const additionsGroupPosition =
          additionsGroups?.findIndex(group => group.id === selectedStepsId.additionsGroupId) ?? -1;
        const additionsGroupId = selectedStepsId.additionsGroupId;
        return {
          visitedStepCategoryId: selectedStepsId.categoryId,
          position,
          selectableCategoryIds,
          simpleProductId: selectedStepsId.simpleProductId,
          additionsGroupId,
          additionsGroupPosition,
          additionsGroups,
        };
      }),
      scan((acc, value) => {
        if (!value) {
          return acc;
        }
        if (!acc) {
          return value;
        }
        const isSameProduct = acc.simpleProductId === value.simpleProductId;
        if (
          value.position > acc.position ||
          (isSameProduct && value.additionsGroupPosition > acc.additionsGroupPosition)
        ) {
          return value;
        }
        return acc;
      }),
    ),
  );

  /**
   * Computed signal that determines whether auto next step should be blocked for declinable products.
   * Returns true if the user is navigating backwards through categories or addition groups.
   */
  public readonly blockAutoNextStep = computed(() => {
    const selectedStepsId = this.declinableProductStepperStore.selectedStepsId();
    const currentCategoryId = selectedStepsId?.categoryId;
    if (!currentCategoryId) {
      return false;
    }
    const categoryIdStepBeforeBack = this.categoryIdStepBeforeBack();
    if (!categoryIdStepBeforeBack) {
      return false;
    }
    const currentCategoryPosition =
      categoryIdStepBeforeBack.selectableCategoryIds.indexOf(currentCategoryId);
    if (currentCategoryPosition < categoryIdStepBeforeBack.position) {
      return true;
    }

    if (currentCategoryPosition === categoryIdStepBeforeBack.position) {
      const isSameProduct =
        categoryIdStepBeforeBack.simpleProductId === selectedStepsId.simpleProductId;
      if (!isSameProduct) {
        // maybe if their is a category where there is multiples choice of product with additionGroup it will not working as expected
        return false;
      }
      const additionGroupPosition = categoryIdStepBeforeBack.additionsGroupPosition;
      const currentAdditionGroupPosition =
        categoryIdStepBeforeBack.additionsGroups?.findIndex(
          group => group.id === selectedStepsId.additionsGroupId,
        ) ?? -1;
      if (currentAdditionGroupPosition < additionGroupPosition) {
        return true;
      }
      return false;
    }
    return false;
  });
}
